/**
 * @file useScreenSlots.ts
 * @description Места для секций на экранах магазина (phase19.16 Р2 — «Блог»:
 * над лентой, под лентой, под статьёй; SB40, SB103; Р2а — «Каталог»: над и
 * под списком; контракт §16.5, §19.6): список мест (`GET /slots`) и блоки
 * каждого места (страница места
 * читается обычным `GET /pages/:id`). Страница места создаётся сервером при
 * первом обращении (`GET /slots/:slot`) — только когда человек добавляет
 * первый блок (`open`).
 */

import type { SiteSlotKey } from "@chaiberi/shared";
import { useCallback, useEffect, useState } from "react";

import { sitePages, type SitePageBlock, type SiteSlotItem } from "../../lib/api-site-pages";

export interface ScreenSlotView extends SiteSlotItem {
  blocks: SitePageBlock[];
}

export interface ScreenSlotsApi {
  slots: ScreenSlotView[] | null;
  error: boolean;
  reload: () => Promise<void>;
  /** id страницы места (создаётся при первом обращении). */
  open: (slot: SiteSlotKey) => Promise<string>;
}

export function useScreenSlots(enabled: boolean): ScreenSlotsApi {
  const [slots, setSlots] = useState<ScreenSlotView[] | null>(null);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    try {
      const list = await sitePages.slots();
      const withBlocks = await Promise.all(
        list.map(async (item) => ({ ...item, blocks: item.pageId && item.blocksCount > 0 ? (await sitePages.findOne(item.pageId)).blocks : [] })),
      );
      setSlots(withBlocks);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const open = useCallback(async (slot: SiteSlotKey) => {
    const known = slots?.find((s) => s.slot === slot)?.pageId;
    if (known) return known;
    return (await sitePages.slot(slot)).id;
  }, [slots]);

  return { slots, error, reload, open };
}
